/**
 * 1.4.3 — live contrast fix for text the scan measured below 4.5:1 (3:1 large).
 *
 * The colours are measured HERE, at runtime, not taken from the scan: the page
 * may have changed, and a stored colour would be wrong on the next redesign.
 * The text colour is moved toward black (light background) or white (dark
 * background) only as far as needed to pass, so the brand hue survives.
 *
 * Applied as an inline `color … !important` through the CSSOM (allowed under a
 * strict style-src CSP), and lifted while the visitor's own contrast mode is on
 * (html.nag-feat-contrast) — "high" mode paints white on black and our colour
 * would fight it.
 */
import { parseRGBA, contrastRatio, effectiveBg, passingColor, type RGB } from '@nagisot/wcag-rules/contrast';

const fixedColor = new Map<HTMLElement, string>();
let observing = false;

function userContrastOn(): boolean {
  return document.documentElement.classList.contains('nag-feat-contrast');
}

function watchUserContrast(): void {
  if (observing || typeof MutationObserver === 'undefined') return;
  observing = true;
  new MutationObserver(() => {
    const on = userContrastOn();
    for (const [el, color] of fixedColor) {
      if (!el.isConnected) { fixedColor.delete(el); continue; }
      if (on) el.style.removeProperty('color');
      else el.style.setProperty('color', color, 'important');
    }
  }).observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
}

/** Returns false while styles may not be final yet (caller retries later). */
export function applyContrastFix(el: Element, target: number): boolean {
  if (!(el instanceof HTMLElement)) return true;
  if (document.readyState === 'loading') return false; // stylesheets may still be arriving
  if (fixedColor.has(el)) return true;
  const fgA = parseRGBA(getComputedStyle(el).color);
  const bg = effectiveBg(el);
  if (!fgA || fgA[3] < 0.1 || !bg) return true; // can't measure reliably — leave it alone
  const fg: RGB = fgA[3] < 0.999
    ? [0, 1, 2].map((i) => Math.round(fgA[i]! * fgA[3] + bg[i]! * (1 - fgA[3]))) as RGB
    : [fgA[0], fgA[1], fgA[2]];
  const goal = (target === 3 ? 3 : 4.5) + 0.1; // small margin over the threshold (rounding, AA tools)
  if (contrastRatio(fg, bg) >= goal) return true; // already passes (site fixed it)
  const c = passingColor(fg, bg, goal);
  const color = `rgb(${c[0]}, ${c[1]}, ${c[2]})`;
  fixedColor.set(el, color);
  watchUserContrast();
  if (!userContrastOn()) el.style.setProperty('color', color, 'important');
  return true;
}
